import React, { useMemo, useCallback } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, Image } from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useFocusEffect } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchRoommates } from '@/store/slices/residents/roommatesSlice';
import { ImageCarousel } from '@/components/residents/bulletins/ImageCarousel';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { profileStrings, strings } from '@/constants/residents/strings';
import { AvatarCircle } from '@/components/common/AvatarCircle';

type RootNav = StackNavigationProp<RootStackParamList>;

export const RoommatesScreen: React.FC = () => {
  const { colors, components } = useThemeColors();
  const styles = useThemedStyles(makeStyles);
  const user = useAppSelector((s) => (s as any).auth?.user);
  const roommatesState = useAppSelector((s) => (s as any).roommates);
  const roommates = Array.isArray(roommatesState?.roommates) ? roommatesState.roommates : ((user as any)?.roommates || []);
  const navigation = useNavigation<RootNav>();
  const dispatch = useAppDispatch();

  useFocusEffect(
    useCallback(() => {
      dispatch(fetchRoommates());
      return () => {};
    }, [dispatch])
  );

  const data = useMemo(() => {
    return Array.isArray(roommates) ? roommates : [];
  }, [roommates]);

  const onPressRoommate = useCallback((roommate: any) => {
    navigation.navigate('RoommateDetail', { roommate });
  }, [navigation]);

  // Optional header carousel (parity with iOS cover photos)
  const coverFiles = useMemo(() => {
    const files = roommatesState?.coverPhotos || (user as any)?.roommates?.coverPhotos || (user as any)?.coverPhotos || [];
    // Map to File[] shape expected by ImageCarousel if necessary
    return Array.isArray(files)
      ? files.map((p: any, idx: number) => ({ id: p.id ?? idx, label: `cover_${idx}.jpg`, fileUrl: p.photoURL || p.photoUrl || p.url || p.fileUrl }))
      : [];
  }, [roommatesState?.coverPhotos, user]);

  return (
    <View style={styles.container}>
      {coverFiles.length > 0 && (
        <View style={styles.carouselWrapper}>
          <ImageCarousel files={coverFiles} />
        </View>
      )}
      <FlatList
        data={data}
        keyExtractor={(_, i) => String(i)}
        renderItem={({ item }) => {
          const first = item?.data?.account?.firstName || item?.firstName || '';
          const last = item?.data?.account?.lastName || item?.lastName || '';
          const name = `${first} ${last}`.trim() || 'Roommate';
          const apt = item?.data?.account?.unitNumberSpace || item?.data?.account?.unitNumber || item?.unitNumberSpace || item?.unitNumber || '';
          const photo = item?.data?.account?.photoUrl || item?.data?.account?.profilePictureUrl || item?.photoUrl || item?.profilePictureUrl;
          return (
            <TouchableOpacity style={styles.cell} onPress={() => onPressRoommate(item)} accessibilityRole="button">
              <AvatarCircle
                firstName={first}
                lastName={last}
                size={36}
                photoUrl={photo}
                useHashedColor={false}
                placeholderColor={components.avatar.background}
                imageBackgroundColor={components.avatar.background}
                initialsColor={colors.text.primary}
                initialsStyle={{ fontWeight: '700' }}
              />
              <View style={styles.info}>
                <Text style={styles.name}>{name}</Text>
                {!!apt && <Text style={styles.sub}>{apt}</Text>}
              </View>
              <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT width={14} height={14} />
            </TouchableOpacity>
          );
        }}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Text style={styles.emptyText}>No roommates found</Text>
          </View>
        }
        ItemSeparatorComponent={() => <View style={styles.separator} />}
      />
    </View>
  );
};

const makeStyles = ({ colors, components }: ThemeContextValue) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg.base,
  },
  carouselWrapper: {
    marginBottom: 12,
  },
  cell: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  avatarSmall: {
    height: 36,
    width: 36,
    borderRadius: 18,
    backgroundColor: components.avatar.background,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarImage: {
    height: 36,
    width: 36,
    borderRadius: 18,
    backgroundColor: components.avatar.background,
  },
  avatarSmallText: {
    fontWeight: '700',
    color: colors.text.primary,
  },
  info: {
    marginLeft: 12,
    flex: 1,
  },
  name: {
    fontSize: 15,
    color: colors.text.primary,
  },
  sub: {
    fontSize: 13,
    color: colors.text.primary,
    opacity: 0.8,
    marginTop: 2,
  },
  empty: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  emptyText: {
    color: colors.text.primary,
    opacity: 0.8,
  },
  separator: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.separator,
  },
});

export default RoommatesScreen;
